import { useState, useEffect, useCallback, lazy, Suspense, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Search, TrendingUp, TrendingDown, BookmarkPlus, Bookmark, Loader2, BarChart3, LineChart, Activity, Users, Target, Newspaper, Landmark, ExternalLink, ChevronDown, ChevronUp, X, RefreshCw, Clock, Home, ChevronRight, Moon, Sunrise } from "lucide-react";
import { API } from "../config/api";
import { useLiveQuote, getLiveQuote, LiveSourceLabel } from "../lib/liveQuote";

// Eager load critical components (removed TradingViewWidget)
import SentimentCard from "../components/SentimentCard";
import SentimentFast from "../components/SentimentFast";
import PricePredictionCompact from "../components/PricePredictionCompact";
import TickerTape from "../components/TickerTape";
import TradingProfileSettings from "../components/TradingProfileSettings";
import MasterSignal from "../components/MasterSignal";
import ExtendedHoursData from "../components/ExtendedHoursData";
import FreshnessDot from "../components/FreshnessDot";
import QuickAlertButton from "../components/QuickAlertButton";
import VwapStatusBadge from "../components/VwapStatusBadge";
import BenchmarkGapCard from "../components/BenchmarkGapCard";
import ETFHoldings from "../components/ETFHoldings";
import CompanyNews from "../components/CompanyNews";
import SixtyDayHistory from "../components/SixtyDayHistory";
import NextDayDirection from "../components/NextDayDirection";
import MeaningfulEdge from "../components/MeaningfulEdge";
import NextDaySentimentPrediction from "../components/NextDaySentimentPrediction";
import StockPimpPredicts from "../components/StockPimpPredicts";
import EarlyWarningsCard from "../components/EarlyWarningsCard";
import AIAnalysisChat from "../components/AIAnalysisChat";
import MultiBenchmarkChart from "../components/MultiBenchmarkChart";
import PEValuationPanel from "../components/PEValuationPanel";
import ChartExplainChat from "../components/ChartExplainChat";
import OptionsAISuggestions from "../components/OptionsAISuggestions";
import QuickReturnsBar from "../components/QuickReturnsBar";
import NavBookValueBadge from "../components/NavBookValueBadge";

// Lazy load heavy components for better performance
const AnalystTargets = lazy(() => import("../components/AnalystTargets"));
const Fundamentals = lazy(() => import("../components/Fundamentals"));
const IntradayPatterns = lazy(() => import("../components/IntradayPatterns"));
const MonthlyPatterns = lazy(() => import("../components/MonthlyPatterns"));
const OverallSentiment = lazy(() => import("../components/OverallSentiment"));
const WeeklyPatterns = lazy(() => import("../components/WeeklyPatterns"));
const NewsAggregator = lazy(() => import("../components/NewsAggregator"));
const SECFilings = lazy(() => import("../components/SECFilings"));
const GovernmentContracts = lazy(() => import("../components/GovernmentContracts"));
const OptionsData = lazy(() => import("../components/OptionsData"));
const TodaysProbability = lazy(() => import("../components/TodaysProbability"));
const PoliticalTrades = lazy(() => import("../components/PoliticalTrades"));
const DarkPoolData = lazy(() => import("../components/DarkPoolData"));
const ContrarianTraders = lazy(() => import("../components/ContrarianTraders"));
const TechnicalIndicators = lazy(() => import("../components/TechnicalIndicators"));
const MovingAverages = lazy(() => import("../components/MovingAverages"));
const ProfitTakingRisk = lazy(() => import("../components/ProfitTakingRisk"));
const ShortTermRisk = lazy(() => import("../components/ShortTermRisk"));
const IntradayPriceAction = lazy(() => import("../components/IntradayPriceAction"));
const PremarketRangeBacktest = lazy(() => import("../components/PremarketRangeBacktest"));
const IntradayConfluenceMap = lazy(() => import("../components/IntradayConfluenceMap"));
const OptionsStrategy = lazy(() => import("../components/OptionsStrategy"));
const TradingSignals = lazy(() => import("../components/TradingSignals"));
const IntradayTradingSignals = lazy(() => import("../components/IntradayTradingSignals"));
const IntradayOptionsStrategy = lazy(() => import("../components/IntradayOptionsStrategy"));
const TickLevelData = lazy(() => import("../components/TickLevelData"));
const YahooQuoteWidget = lazy(() => import("../components/YahooQuoteWidget"));
const SeasonalMonthlyAnalysis = lazy(() => import("../components/SeasonalMonthlyAnalysis"));
const StockIndicatorsChart = lazy(() => import("../components/StockIndicatorsChart"));
const StockVixChart = lazy(() => import("../components/StockVixChart"));
const IntradayCandlestick = lazy(() => import("../components/IntradayCandlestick"));
const IntradayPatternsChart = lazy(() => import("../components/IntradayPatternsChart"));
const PremarketHighLowCard = lazy(() => import("../components/PremarketHighLowCard"));
const IntradayPeriodicityChart = lazy(() => import("../components/IntradayPeriodicityChart"));
const PriceTrendChart = lazy(() => import("../components/PriceTrendChart"));
const SectorPerformance = lazy(() => import("../components/SectorPerformance"));
const ValuationAnalysis = lazy(() => import("../components/ValuationAnalysis"));
const AbnormalActivity = lazy(() => import("../components/AbnormalActivity"));
const ComprehensiveSentiment = lazy(() => import("../components/ComprehensiveSentiment"));
const PredictiveAnalytics = lazy(() => import("../components/PredictiveAnalytics"));
const NewsSentiment = lazy(() => import("../components/NewsSentiment"));
const PremarketPatternChart = lazy(() => import("../components/PremarketPatternChart"));
import LazyOnScroll from "../components/LazyOnScroll";

// Loading component for lazy-loaded components
const ComponentLoader = () => (
  <div className="flex items-center justify-center py-6">
    <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
  </div>
);

const Dashboard = () => {
  const [searchParams] = useSearchParams();
  const [searchTicker, setSearchTicker] = useState("");
  const [currentTicker, setCurrentTicker] = useState("QQQ");
  // Generation token: every new fetchStockData() call bumps this and any older
  // in-flight fetch checks it before writing state — guards against a slow
  // previous ticker (e.g. SMH with 25 holdings, 60s) landing AFTER a newer
  // search (SNDK) and clobbering it.
  const fetchGenRef = useRef(0);
  const [stockData, setStockData] = useState(null);
  const [stockDataLastUpdate, setStockDataLastUpdate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [watchlist, setWatchlist] = useState([]);
  const [inWatchlist, setInWatchlist] = useState(false);
  // NAV (ETF) or Book Value (stock) per share — used to overlay a
  // fair-value reference line on the Multi-Benchmark chart.
  const [navBookValue, setNavBookValue] = useState(null);
  const [peValuation, setPeValuation] = useState(null); // P/E panel summary + AI read → fed into AI chat context
  const [aiChatOpen, setAiChatOpen] = useState(false);  // AI chat mounts only on click (no API calls on page load)
  const { quote: liveQ } = useLiveQuote(currentTicker);  // SINGLE shared price / 1D source (MSN, 30s cache)
  const [indReturns, setIndReturns] = useState(null); // MFI/RSI/VWAP/MACD table → fed into AI chat context

  // Per-ticker indicator returns for AI chat grounding (backend caches 2 min)
  useEffect(() => {
    if (!currentTicker) return undefined;
    let cancelled = false;
    setIndReturns(null);
    axios.get(`${API}/stock/indicator-returns/${encodeURIComponent(currentTicker)}`, { timeout: 45000 })
      .then(r => {
        if (cancelled || !r.data?.rows?.length) return;
        setIndReturns({
          ticker: r.data.ticker,
          windows: r.data.windows,
          rows: r.data.rows.map(x => ({ key: x.key, name: x.name, current: x.current, note: x.note, values: x.values })),
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentTicker]);
  const [basketHealthLoaded, setBasketHealthLoaded] = useState(false);
  const [basketHealthData, setBasketHealthData] = useState(null);
  const [signalRefreshKey, setSignalRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState("charts"); // Track active tab for lazy loading - Start with Charts
  const [loadedTabs, setLoadedTabs] = useState(new Set(["charts"])); // Track which tabs have been viewed - Start with Charts
  const [chartTimeframe, setChartTimeframe] = useState('1d'); // Shared timeframe for all charts in Charts tab
  const [timeframeChanges, setTimeframeChanges] = useState({}); // Percentage changes for each timeframe
  
  // News state - copied from MarketOverviewPage
  const [news, setNews] = useState([]);
  const [politicalNews, setPoliticalNews] = useState([]);
  const [marketNews, setMarketNews] = useState([]);
  const [lastMarketDriver, setLastMarketDriver] = useState(null);
  const [newsDropdownOpen, setNewsDropdownOpen] = useState(false);
  const [tickerPaused, setTickerPaused] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [newsLoading, setNewsLoading] = useState(false);
  const tickerRef = useRef(null);

  // Fetch news data - Load on mount for Market Driver display
  const fetchNewsData = useCallback(async () => {
    if (newsLoading) return;
    setNewsLoading(true);
    try {
      // Fetch news in parallel
      const [newsRes, politicalRes] = await Promise.all([
        axios.get(`${API}/stock/news-aggregator/SPY`).catch(() => ({ data: { news: [] } })),
        axios.get(`${API}/market/political-news`).catch(() => ({ data: { news: [] } })),
      ]);
      
      // Filter news to only show items from last 2 HOURS for ticker
      const twoHoursAgo = new Date();
      twoHoursAgo.setHours(twoHoursAgo.getHours() - 2);
      
      const filterRecentNews = (newsItems) => {
        return (newsItems || [])
          .filter(item => {
            if (!item.published) return true;
            try {
              const pubDate = new Date(item.published);
              return pubDate >= twoHoursAgo;
            } catch {
              return true;
            }
          })
          .sort((a, b) => {
            try {
              const dateA = new Date(a.published || 0);
              const dateB = new Date(b.published || 0);
              return dateB - dateA;
            } catch {
              return 0;
            }
          });
      };
      
      // Filter for Market Headlines section (last 24 hours - more lenient)
      const twentyFourHoursAgo = new Date();
      twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);
      
      const filterMarketNews = (newsItems) => {
        return (newsItems || [])
          .filter(item => {
            if (!item.published) return true;
            try {
              const pubDate = new Date(item.published);
              return pubDate >= twentyFourHoursAgo;
            } catch {
              return true;
            }
          })
          .sort((a, b) => {
            try {
              const dateA = new Date(a.published || 0);
              const dateB = new Date(b.published || 0);
              return dateB - dateA;
            } catch {
              return 0;
            }
          });
      };
      
      // Use just the fetched news (reduced API calls)
      const allMarketNews = newsRes.data?.news || [];
      
      // Remove duplicates by title
      const seenTitles = new Set();
      const uniqueMarketNews = allMarketNews.filter(item => {
        const titleKey = item.title?.toLowerCase().substring(0, 50);
        if (seenTitles.has(titleKey)) return false;
        seenTitles.add(titleKey);
        return true;
      });
      
      const filteredPoliticalNews = filterRecentNews(politicalRes.data?.news);
      const filteredMarketNewsRecent = filterRecentNews(uniqueMarketNews);
      const filteredMarketNewsDaily = filterMarketNews(uniqueMarketNews);
      
      // Cache the market driver with cascading fallback
      if (filteredPoliticalNews.length > 0) {
        setLastMarketDriver(filteredPoliticalNews[0]);
        try {
          localStorage.setItem('lastMarketDriver', JSON.stringify({
            ...filteredPoliticalNews[0],
            cachedAt: new Date().toISOString()
          }));
        } catch (e) { /* ignore */ }
      } else if (filteredMarketNewsRecent.length > 0) {
        setLastMarketDriver(filteredMarketNewsRecent[0]);
        try {
          localStorage.setItem('lastMarketDriver', JSON.stringify({
            ...filteredMarketNewsRecent[0],
            cachedAt: new Date().toISOString()
          }));
        } catch (e) { /* ignore */ }
      } else if (filteredMarketNewsDaily.length > 0) {
        setLastMarketDriver(filteredMarketNewsDaily[0]);
        try {
          localStorage.setItem('lastMarketDriver', JSON.stringify({
            ...filteredMarketNewsDaily[0],
            cachedAt: new Date().toISOString()
          }));
        } catch (e) { /* ignore */ }
      } else if (!lastMarketDriver) {
        try {
          const cached = localStorage.getItem('lastMarketDriver');
          if (cached) setLastMarketDriver(JSON.parse(cached));
        } catch (e) { /* ignore */ }
      }
      
      setNews(filterRecentNews(newsRes.data?.news));
      setMarketNews(filterMarketNews(uniqueMarketNews));
      setPoliticalNews(filteredPoliticalNews);
      setLastUpdate(new Date());
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setNewsLoading(false);
    }
  }, [newsLoading, lastMarketDriver]);

  // Load news on mount for Market Driver display
  useEffect(() => {
    // Load cached market driver from localStorage immediately
    try {
      const cached = localStorage.getItem('lastMarketDriver');
      if (cached) {
        const parsed = JSON.parse(cached);
        // Only use if less than 1 hour old
        const cachedAt = new Date(parsed.cachedAt);
        if (new Date() - cachedAt < 3600000) {
          setLastMarketDriver(parsed);
        }
      }
    } catch (e) { /* ignore */ }
    
    // Fetch fresh news data after a short delay (don't block initial render)
    const timer = setTimeout(() => {
      fetchNewsData();
    }, 500);
    // Retry + periodic refresh so breaking news recovers from cold-start failures
    const retry = setTimeout(() => fetchNewsData(), 20000);
    const interval = setInterval(() => fetchNewsData(), 180000);
    
    return () => { clearTimeout(timer); clearTimeout(retry); clearInterval(interval); };
  }, []);

  // Format relative time - copied from MarketOverviewPage
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return "";
    }
  };

  // Combine political news + market news for the ticker
  const allNewsForTicker = [...politicalNews, ...marketNews, ...news]
    .filter((item, idx, arr) => {
      const titleKey = item.title?.toLowerCase().substring(0, 50);
      return arr.findIndex(i => i.title?.toLowerCase().substring(0, 50) === titleKey) === idx;
    })
    .slice(0, 30);

  // Read ticker from URL params on mount and fetch data
  useEffect(() => {
    const tickerParam = searchParams.get('ticker');
    if (tickerParam) {
      const ticker = tickerParam.toUpperCase();
      setSearchTicker(ticker);
      setCurrentTicker(ticker);
      // Fetch stock data for the ticker from URL
      const fetchFromUrl = async () => {
        setLoading(true);
        try {
          const response = await axios.post(`${API}/stock/info`, { ticker });
          setStockData(response.data);
          setStockDataLastUpdate(new Date());
        } catch (error) {
          console.error("Error fetching stock data from URL param:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchFromUrl();
    }
  }, [searchParams]);
  const [tradingProfile, setTradingProfile] = useState({
    strategy: "intraday_etf_options",
    strategies: ["intraday_etf_options"],
    accountSize: "25000",
    dtePreference: "1-3dte",
    riskPerTrade: "1"
  });

  // Reset basket health loaded state when ticker changes
  useEffect(() => {
    setBasketHealthLoaded(false);
    setBasketHealthData(null);
  }, [currentTicker]);

  // Callback for when basket health finishes loading
  const handleBasketHealthLoaded = useCallback((basketData) => {
    setBasketHealthLoaded(true);
    setBasketHealthData(basketData);
  }, []);

  // Callback for trading profile changes
  const handleTradingProfileChange = useCallback((newSettings) => {
    setTradingProfile(newSettings);
  }, []);

  // Callback for recalculating signals
  const handleRecalculateSignals = useCallback(() => {
    setSignalRefreshKey(prev => prev + 1);
  }, []);

  const fetchWatchlist = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/watchlist`);
      setWatchlist(response.data.watchlist || []);
    } catch (error) {
      console.error("Error fetching watchlist:", error);
    }
  }, []);

  const fetchStockData = useCallback(async (ticker) => {
    if (!ticker) return;
    const tickerU = ticker.toUpperCase();
    const LS_KEY = `sp_stock_info_cache_${tickerU}`;
    // Generation token for stale-write protection. Any earlier in-flight
    // fetch (different ticker) will see its `myGen` no longer matches and
    // bail out before overwriting state.
    const myGen = ++fetchGenRef.current;
    const isStale = () => fetchGenRef.current !== myGen;

    // Serve any cached payload IMMEDIATELY (no age limit) so the page never
    // shows the "Search for a stock" empty state when the user has visited
    // this ticker before. Background refresh keeps the data current.
    let servedFromCache = false;
    try {
      const cached = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
      if (cached?.data && !isStale()) {
        setStockData({ ...cached.data, _stale: Date.now() - cached.ts > 3600 * 1000 });
        setStockDataLastUpdate(new Date(cached.ts));
        setCurrentTicker(tickerU);
        servedFromCache = true;
      }
    } catch {}

    if (!servedFromCache) setLoading(true);

    let lastErr = null;
    const TIMEOUTS = [25000, 35000, 45000, 60000];
    for (let attempt = 0; attempt < TIMEOUTS.length; attempt++) {
      if (isStale()) return; // newer fetch took over — abort silently
      try {
        const response = await axios.post(
          `${API}/stock/info`, { ticker },
          { timeout: TIMEOUTS[attempt] },
        );
        if (isStale()) return;
        if (response.data) {
          setStockData(response.data);
          setStockDataLastUpdate(new Date());
          setCurrentTicker(tickerU);
          try { localStorage.setItem(LS_KEY, JSON.stringify({ data: response.data, ts: Date.now() })); } catch {}
          if (!servedFromCache && attempt === 0) toast.success(`Loaded ${tickerU}`);
          setLoading(false);
          return;
        }
      } catch (error) {
        lastErr = error;
        if (isStale()) return;
        console.error(`fetchStockData ${tickerU} attempt ${attempt + 1} failed:`, error.message);
        if (attempt < TIMEOUTS.length - 1) {
          await new Promise(r => setTimeout(r, (attempt + 1) * 1500));
        }
      }
    }
    if (isStale()) return;
    // All attempts failed.
    if (!servedFromCache) {
      try {
        const cached = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
        if (cached?.data && !isStale()) {
          setStockData({ ...cached.data, _stale: true });
          setStockDataLastUpdate(new Date(cached.ts));
          setCurrentTicker(tickerU);
        }
      } catch {}
    }
    console.warn(`fetchStockData ${tickerU}: live fetch failed (silent retry in background)`, lastErr?.message);
    // Surface a toast so the user knows the search didn't silently no-op.
    toast.error(`Couldn't load ${tickerU} — retrying in background`);
    setTimeout(() => { if (!isStale()) fetchStockData(tickerU); }, 8000);
    setLoading(false);
  }, []);

  // Fetch percentage changes for all timeframes
  const fetchTimeframeChanges = useCallback(async (ticker) => {
    if (!ticker) return;
    try {
      const response = await axios.get(`${API}/stock/timeframe-changes/${ticker}`, {
        timeout: 30000
      });
      if (response.data && response.data.changes) {
        const lq = await getLiveQuote(ticker);  // 1D ALWAYS from the shared MSN quote
        setTimeframeChanges(lq?.change_1d_pct != null ? { ...response.data.changes, "1d": lq.change_1d_pct } : response.data.changes);
      }
    } catch (error) {
      console.error("Error fetching timeframe changes:", error);
      setTimeframeChanges({});
    }
  }, []);

  // Fetch timeframe changes when ticker changes
  useEffect(() => {
    if (currentTicker) {
      fetchTimeframeChanges(currentTicker);
    }
  }, [currentTicker, fetchTimeframeChanges]);

  // Prefetch batch data when ticker is entered
  const prefetchBatchData = async (ticker) => {
    try {
      // Fire and forget - don't await, just prefetch
      axios.post(`${API}/stock/batch-data/${ticker}`).catch(() => {});
    } catch (error) {
      // Silent fail - this is just prefetching
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTicker.trim()) {
      // Prefetch batch data immediately
      prefetchBatchData(searchTicker.trim());
      // Then fetch main stock info
      fetchStockData(searchTicker.trim());
    }
  };

  const toggleWatchlist = async () => {
    if (inWatchlist) {
      try {
        await axios.delete(`${API}/watchlist/${currentTicker}`);
        toast.success(`Removed ${currentTicker} from watchlist`);
        setInWatchlist(false);
        fetchWatchlist();
      } catch (error) {
        toast.error("Failed to remove from watchlist");
      }
    } else {
      try {
        await axios.post(`${API}/watchlist/add`, { ticker: currentTicker });
        toast.success(`Added ${currentTicker} to watchlist`);
        setInWatchlist(true);
        fetchWatchlist();
      } catch (error) {
        toast.error("Failed to add to watchlist");
      }
    }
  };

  const formatNumber = (num) => {
    if (!num) return "N/A";
    if (num >= 1e12) return `$${(num / 1e12).toFixed?.(2) || "0.00"}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed?.(2) || "0.00"}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed?.(2) || "0.00"}M`;
    return `$${num.toLocaleString()}`;
  };

  // Initialize component
  useEffect(() => {
    fetchWatchlist();
    // Only load default QQQ if no ticker in URL
    const tickerParam = searchParams.get('ticker');
    if (!tickerParam) {
      fetchStockData("QQQ"); // Load default stock
    }
  }, [fetchWatchlist, fetchStockData, searchParams]);

  // Check if current ticker is in watchlist
  useEffect(() => {
    setInWatchlist(watchlist.some(item => item.ticker === currentTicker));
  }, [watchlist, currentTicker]);

  // Fetch NAV / Book value whenever the current ticker changes so the
  // Multi-Benchmark chart can render a fair-value reference line.  For
  // ETFs during market hours we ALSO poll /inav-live every 30s so the
  // chart's fair-value line tracks live basket movement.
  useEffect(() => {
    if (!currentTicker) { setNavBookValue(null); return; }
    let cancelled = false;
    let pollHandle = null;

    const applyLive = async () => {
      try {
        const r = await axios.get(
          `${API}/stock/inav-live/${encodeURIComponent(currentTicker)}`,
          { timeout: 12000 }
        );
        if (cancelled) return;
        setNavBookValue((d) => (d && d.type === "ETF" && r.data?.inav_live)
          ? { ...d,
              value_per_share: r.data.inav_live,
              inav_live: r.data.inav_live,
              basket_change_pct: r.data.basket_change_pct,
              coverage_pct: r.data.coverage_pct,
              n_holdings_priced: r.data.n_holdings,
              market_state: r.data.market_state,
              current_price: r.data.current_price ?? d.current_price,
              premium_pct: r.data.premium_pct ?? d.premium_pct,
              computed_at: r.data.fetched_at }
          : d);
      } catch (_) { /* keep last known */ }
    };

    axios
      .get(`${API}/stock/nav-book-value/${encodeURIComponent(currentTicker)}`, { timeout: 15000 })
      .then((r) => {
        if (cancelled) return;
        const d = r.data || null;
        setNavBookValue(d);
        if (d && d.type === "ETF" && d.inav_live && (d.market_state === "REGULAR" || d.market_state === "PRE" || d.market_state === "POST")) {
          pollHandle = setInterval(applyLive, 30000);
        }
      })
      .catch(() => { if (!cancelled) setNavBookValue(null); });

    return () => {
      cancelled = true;
      if (pollHandle) clearInterval(pollHandle);
    };
  }, [currentTicker]);

  // Handler for ticker tape clicks
  const handleTickerTapeClick = useCallback((symbol) => {
    setSearchTicker(symbol);
    setCurrentTicker(symbol);
    fetchStockData(symbol);
    toast.success(`Switched to ${symbol}`);
  }, [fetchStockData]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-200">
      <Toaster position="top-right" />
      
      {/* Finviz-Style Header */}
      <header className="bg-[#1a1a2e] border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-[1920px] mx-auto px-2">
          {/* Top Row */}
          <div className="flex items-center justify-between h-10">
            <div className="flex items-center gap-4">
              <a href="/" className="flex items-center gap-2" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://customer-assets.emergentagent.com/job_6ab71d84-1c0a-4dfc-96a7-091e2ccdbc04/artifacts/n9h4u4r1_Stock%20Pimp%20Logo.png" 
                  alt="StockPimp AI Logo" 
                  className="w-7 h-7 rounded"
                />
                <span className="text-base font-bold text-white">
                  <span className="text-emerald-400">STOCK</span>PIMP
                  <span className="text-xs text-gray-500 ml-1">AI</span>
                </span>
              </a>
              <nav className="hidden md:flex items-center gap-1 text-xs">
                <a href="/" className="px-2 py-1 text-gray-400 hover:text-white hover:bg-gray-800 rounded" target="_blank" rel="noopener noreferrer">Market</a>
                <span className="px-2 py-1 text-white bg-gray-800 rounded">Quote</span>
                <a href="/scanner" className="px-2 py-1 text-gray-400 hover:text-white hover:bg-gray-800 rounded" target="_blank" rel="noopener noreferrer">Screener</a>
                <a href="/portfolio" className="px-2 py-1 text-gray-400 hover:text-white hover:bg-gray-800 rounded" target="_blank" rel="noopener noreferrer">Portfolio</a>
              </nav>
            </div>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex items-center gap-2">
              <div className="relative">
                <Input
                  data-testid="ticker-search-input"
                  type="text"
                  placeholder="Ticker..."
                  value={searchTicker}
                  onChange={(e) => setSearchTicker(e.target.value.toUpperCase())}
                  className="w-28 sm:w-40 h-7 text-xs bg-[#111] border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 rounded px-2"
                />
              </div>
              <Button 
                data-testid="search-button"
                type="submit" 
                size="sm"
                className="h-7 px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
              >
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
              </Button>
              <button
                type="button"
                onClick={toggleWatchlist}
                className={`h-7 w-7 flex items-center justify-center rounded ${inWatchlist ? 'bg-yellow-600 text-white' : 'bg-gray-700 text-gray-400 hover:text-white'}`}
                title={inWatchlist ? 'Remove from watchlist' : 'Add to watchlist'}
              >
                {inWatchlist ? <Bookmark className="w-3.5 h-3.5" /> : <BookmarkPlus className="w-3.5 h-3.5" />}
              </button>
            </form>
          </div>
        </div>
      </header>
      
      {/* Ticker Tape */}
      <TickerTape onTickerClick={handleTickerTapeClick} />
      
      {/* News Dropdown Panel - copied from MarketOverviewPage */}
      {newsDropdownOpen && (
        <div className="fixed inset-0 z-50" onClick={() => setNewsDropdownOpen(false)}>
          <div 
            className="absolute top-12 right-4 w-[500px] max-w-[95vw] bg-[#111] border border-gray-700 rounded-lg shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="news-dropdown-panel"
          >
            <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-red-900/50 to-[#1a1a2e] border-b border-gray-700">
              <div className="flex items-center gap-2">
                <Landmark className="w-4 h-4 text-red-400" />
                <span className="font-semibold text-white">Latest News</span>
                <span className="text-[10px] text-gray-400">(Last 2 hours)</span>
              </div>
              <button onClick={() => setNewsDropdownOpen(false)} className="text-gray-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto divide-y divide-gray-800">
              {allNewsForTicker.length > 0 ? allNewsForTicker.map((item, idx) => (
                <a
                  key={idx}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-3 hover:bg-gray-800/50 transition-colors group"
                  data-testid={`dropdown-news-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <p className="text-sm text-gray-200 group-hover:text-white leading-tight mb-1">
                        {item.title}
                      </p>
                      <div className="flex items-center gap-2 text-[10px]">
                        <span className={`px-1.5 py-0.5 rounded ${item.is_political ? 'bg-red-900/50 text-red-400' : 'bg-blue-900/50 text-blue-400'}`}>
                          {item.source || 'News'}
                        </span>
                        <span className="text-gray-500">{formatRelativeTime(item.published)}</span>
                      </div>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-gray-500 group-hover:text-blue-400 shrink-0 mt-1" />
                  </div>
                </a>
              )) : (
                <div className="px-4 py-8 text-center text-gray-500">
                  No recent news available
                </div>
              )}
            </div>
            <div className="px-4 py-2 bg-[#0a0a0a] border-t border-gray-700 text-[10px] text-gray-500 flex items-center justify-between">
              <span>Auto-refresh: 1 min</span>
              <span>Sources: Politico, Reuters, CNBC, Yahoo Finance</span>
            </div>
          </div>
        </div>
      )}

      {/* Scrolling News Headline Ticker with Links - copied from MarketOverviewPage */}
      <div className="bg-black border-b border-gray-800 overflow-hidden">
        <div 
          className="max-w-[1920px] mx-auto"
          onMouseEnter={() => setTickerPaused(true)}
          onMouseLeave={() => setTickerPaused(false)}
        >
          <div className="flex items-center">
            {/* Fixed Label */}
            <div className="bg-red-600 text-white px-3 py-1.5 font-bold text-xs flex items-center gap-1.5 shrink-0 z-10">
              <Landmark className="w-3.5 h-3.5" />
              BREAKING
            </div>
            
            {/* Scrolling Ticker */}
            <div className="flex-1 overflow-hidden">
              <div 
                ref={tickerRef}
                className={`flex items-center gap-6 py-1.5 ${tickerPaused ? '' : 'animate-news-ticker'}`}
                style={{
                  animation: tickerPaused ? 'none' : 'newsticker 15s linear infinite',
                }}
              >
                {allNewsForTicker.slice(0, 15).map((item, idx) => (
                  <a
                    key={idx}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 shrink-0 group"
                    data-testid={`ticker-news-${idx}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${item.is_political ? 'bg-red-500' : 'bg-blue-500'}`} />
                    <span className="text-xs text-gray-300 group-hover:text-white whitespace-nowrap max-w-[400px] truncate">
                      {item.title}
                    </span>
                    <span className="text-[10px] text-gray-500 shrink-0">
                      {formatRelativeTime(item.published)}
                    </span>
                    <ExternalLink className="w-3 h-3 text-gray-600 group-hover:text-blue-400" />
                  </a>
                ))}
                {/* Duplicate for seamless loop */}
                {allNewsForTicker.slice(0, 15).map((item, idx) => (
                  <a
                    key={`dup-${idx}`}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 shrink-0 group"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${item.is_political ? 'bg-red-500' : 'bg-blue-500'}`} />
                    <span className="text-xs text-gray-300 group-hover:text-white whitespace-nowrap max-w-[400px] truncate">
                      {item.title}
                    </span>
                    <span className="text-[10px] text-gray-500 shrink-0">
                      {formatRelativeTime(item.published)}
                    </span>
                    <ExternalLink className="w-3 h-3 text-gray-600 group-hover:text-blue-400" />
                  </a>
                ))}
              </div>
            </div>
            
            {/* Dropdown Toggle on Right */}
            <button 
              onClick={() => setNewsDropdownOpen(true)}
              className="bg-[#1a1a2e] text-gray-400 hover:text-white px-3 py-1.5 text-xs flex items-center gap-1 shrink-0 border-l border-gray-700"
            >
              More <ChevronDown className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {/* Add ticker animation styles */}
      <style>{`
        @keyframes newsticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-news-ticker {
          animation: newsticker 60s linear infinite;
        }
        .animate-news-ticker:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* Market Driver Label + Event - copied from MarketOverviewPage */}
      {(() => {
        const currentDriver = politicalNews[0] || marketNews[0] || lastMarketDriver;
        const driverSource = politicalNews[0] ? 'BREAKING' : 
                             marketNews[0] ? 'TODAY' : 
                             lastMarketDriver ? 'LAST KNOWN' : null;
        const isOlderNews = !politicalNews[0] && (marketNews[0] || lastMarketDriver);
        
        return (
          <div className="bg-black border-b border-red-900/50 py-2">
            <div className="max-w-[1920px] mx-auto px-4">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Landmark className="w-3 h-3 text-red-500" />
                <span className="text-[10px] font-bold text-red-400 uppercase tracking-widest">Market Driver</span>
                {driverSource && driverSource !== 'BREAKING' && (
                  <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                    driverSource === 'TODAY' ? 'text-green-400 bg-green-900/30' :
                    'text-yellow-500 bg-yellow-900/30'
                  }`}>{driverSource}</span>
                )}
              </div>
              {currentDriver ? (
                <a 
                  href={currentDriver.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center group"
                >
                  <p className={`text-sm group-hover:text-red-300 transition-colors ${isOlderNews ? 'text-gray-300' : 'text-white'}`}>
                    {currentDriver.title}
                  </p>
                  <div className="flex items-center justify-center gap-2 mt-1">
                    <span className="text-[10px] text-red-400">{currentDriver.source}</span>
                    <span className="text-[10px] text-gray-500">{formatRelativeTime(currentDriver.published)}</span>
                    <ExternalLink className="w-3 h-3 text-gray-500 group-hover:text-red-400" />
                  </div>
                </a>
              ) : (
                <p className="text-sm text-gray-400 text-center">Loading market-driving news...</p>
              )}
            </div>
          </div>
        );
      })()}

      {/* Ticker-aware AI Chat — mounted ONLY after the user opens it (no LLM /
          API calls on page load — user directive Oct 6 2026). */}
      {currentTicker && !aiChatOpen && (
        <div className="max-w-[1920px] mx-auto px-2 mt-2">
          <button type="button" onClick={() => setAiChatOpen(true)} data-testid="ai-chat-open-btn"
            className="w-full flex items-center justify-between gap-3 rounded-md border border-dashed border-purple-800/70 bg-purple-950/20 px-3 py-2.5 text-left text-xs text-slate-300 hover:bg-purple-950/40">
            <span><span className="font-semibold text-purple-200">AI Analyst for {currentTicker}</span> — ask about valuation, momentum, catalysts and risk. Nothing is generated (no tokens spent) until you open it.</span>
            <span className="shrink-0 inline-flex items-center gap-1.5 rounded border border-purple-600 bg-purple-800/60 px-3 py-1.5 text-xs font-semibold text-purple-50">Open AI Analyst</span>
          </button>
        </div>
      )}
      {currentTicker && aiChatOpen && (
        <div className="max-w-[1920px] mx-auto px-2 mt-2">
          <AIAnalysisChat
            mode="stock"
            ticker={currentTicker}
            tickerName={navBookValue?.name || currentTicker}
            tickerType={navBookValue?.type || "Stock"}
            returns={{
              r1d:   timeframeChanges?.["1d"],
              r5d:   timeframeChanges?.["5d"],
              r20d:  timeframeChanges?.["1mo"],
              r50d:  timeframeChanges?.["3mo"],
              r100d: timeframeChanges?.["6mo"],
              r1y:   timeframeChanges?.["1y"],
            }}
            extraContext={{
              price:      navBookValue?.current_price,
              nav_close:  navBookValue?.nav_close,
              inav_live:  navBookValue?.inav_live,
              premium_pct: navBookValue?.premium_pct,
              ...(peValuation ? { valuation: peValuation } : {}),
              ...(indReturns ? { indicators: indReturns } : {}),
            }}
          />
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-[1920px] mx-auto px-2 py-2">
        {loading && !stockData ? (
          <div className="space-y-3">
            {/* Stock header skeleton */}
            <div className="bg-[#111] border border-gray-800 rounded p-3">
              <div className="flex items-center gap-4 mb-3">
                <div className="animate-pulse bg-gray-800/60 rounded h-8 w-20" />
                <div className="animate-pulse bg-gray-800/60 rounded h-8 w-32" />
                <div className="animate-pulse bg-gray-800/60 rounded h-6 w-20 ml-auto" />
              </div>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
                {Array.from({length:8}).map((_,i) => (
                  <div key={i} className="bg-[#0a0a0a] rounded p-2">
                    <div className="animate-pulse bg-gray-800/60 rounded h-2.5 w-1/2 mb-1.5" />
                    <div className="animate-pulse bg-gray-800/60 rounded h-4 w-2/3" />
                  </div>
                ))}
              </div>
            </div>
            {/* Tabs skeleton */}
            <div className="flex gap-2">
              {Array.from({length:5}).map((_,i) => (
                <div key={i} className="animate-pulse bg-gray-800/60 rounded-lg h-8 w-24" />
              ))}
            </div>
            {/* Chart skeleton */}
            <div className="bg-[#111] border border-gray-800 rounded p-4 h-80">
              <div className="animate-pulse bg-gray-800/60 rounded h-4 w-1/4 mb-4" />
              <div className="flex items-end gap-1 h-[85%]">
                {Array.from({length:30}).map((_,i) => (
                  <div key={i} className="animate-pulse bg-gray-800/60 rounded-t flex-1" style={{height: `${15+Math.random()*70}%`}} />
                ))}
              </div>
            </div>
            {/* Bottom cards skeleton */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {[1,2].map(i => (
                <div key={i} className="bg-[#111] border border-gray-800 rounded-lg p-4">
                  <div className="animate-pulse bg-gray-800/60 rounded h-4 w-1/3 mb-3" />
                  {Array.from({length:4}).map((_,j) => (
                    <div key={j} className="animate-pulse bg-gray-800/60 rounded h-3 w-full mb-2" />
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : stockData ? (
          <div className="space-y-2">
            {/* Rate Limit Warning */}
            {stockData.error === 'rate_limited' || stockData.currentPrice === 0 ? (
              <div className="bg-yellow-900/30 border border-yellow-700 rounded p-3 flex items-center gap-3">
                <div className="text-yellow-500">⚠️</div>
                <div>
                  <p className="font-medium text-yellow-400 text-sm">Data temporarily unavailable</p>
                  <p className="text-xs text-yellow-500/80">Yahoo Finance is rate limiting requests. Please wait a minute and refresh.</p>
                </div>
              </div>
            ) : null}
            
            {/* Finviz-Style Stock Header */}
            <div className="bg-[#111] border border-gray-800 rounded">
              {/* Top Section: Ticker + Price */}
              <div className="p-3 border-b border-gray-800">
                <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
                  <div className="flex items-center gap-4">
                    {/* Ticker Symbol */}
                    <div>
                      <div className="flex items-center gap-2">
                        <h1 className="text-2xl sm:text-3xl font-bold text-white" data-testid="stock-ticker">{currentTicker}</h1>
                        <FreshnessDot timestamp={stockDataLastUpdate} size="sm" showLabel={true} />
                        <QuickAlertButton ticker={currentTicker} currentPrice={stockData.currentPrice} />
                        <VwapStatusBadge ticker={currentTicker} currentPrice={stockData.currentPrice} mode="intraday" />
                        <VwapStatusBadge ticker={currentTicker} currentPrice={stockData.currentPrice} mode="anchored" timeframe={chartTimeframe} />
                      </div>
                      <p className="text-xs sm:text-sm text-gray-400">{stockData.companyName}</p>
                    </div>
                    {/* Price Block */}
                    <div className="text-right">
                      {(() => {
                        const hp = liveQ?.price ?? stockData.currentPrice;
                        const hc = liveQ ? liveQ.change_1d : stockData.change;
                        const hpct = liveQ ? liveQ.change_1d_pct : stockData.changePercent;
                        return (<>
                          <div className="text-2xl sm:text-3xl font-bold text-white" data-testid="current-price">
                            ${hp?.toFixed?.(2) || "0.00"}
                          </div>
                          <div className={`flex items-center justify-end gap-2 text-sm sm:text-lg font-semibold ${hc >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {hc >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                            <span data-testid="price-change">
                              {hc >= 0 ? '+' : ''}{hc?.toFixed?.(2) || "0.00"} ({hpct?.toFixed?.(2) || "0.00"}%)
                            </span>
                          </div>
                          <div className="flex justify-end"><LiveSourceLabel quote={liveQ} testId="header-price-source" /></div>
                        </>);
                      })()}
                    </div>
                    {/* Extended Hours Block — Pre-Market (sunrise) / After Hours (moon) */}
                    {(stockData.preMarketPrice || stockData.postMarketPrice) && (() => {
                      const isPre = !!stockData.preMarketPrice;
                      const extPrice = isPre ? stockData.preMarketPrice : stockData.postMarketPrice;
                      const extChange = isPre ? stockData.preMarketChange : stockData.postMarketChange;
                      const extPct = isPre ? stockData.preMarketChangePercent : stockData.postMarketChangePercent;
                      const up = (extChange || 0) >= 0;
                      return (
                        <div className="text-left border-l border-gray-800 pl-4" data-testid="extended-hours-block">
                          <div className={`flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide ${isPre ? 'text-amber-400' : 'text-orange-400'}`}>
                            {isPre
                              ? <Sunrise className="w-3.5 h-3.5" />
                              : <Moon className="w-3.5 h-3.5 fill-orange-400" />}
                            {isPre ? 'Pre-Market' : 'After Hours'}
                          </div>
                          <div className="text-lg sm:text-xl font-bold text-white leading-tight" data-testid="extended-hours-price">
                            ${Number(extPrice).toFixed(2)}
                          </div>
                          <div className={`flex items-center gap-1 text-xs font-semibold ${up ? 'text-green-400' : 'text-red-400'}`}
                               data-testid="extended-hours-change">
                            {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {up ? '+' : ''}{Number(extChange || 0).toFixed(2)} ({up ? '+' : ''}{Number(extPct || 0).toFixed(2)}%)
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  {/* Sector + Actions */}
                  <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
                    <div className="text-left sm:text-right">
                      <div className="flex items-center gap-2 text-xs">
                        <a href={`/stock?ticker=${stockData.sector}`} className="text-blue-400 hover:underline" target="_blank" rel="noopener noreferrer">{stockData.sector}</a>
                        <span className="text-gray-600">•</span>
                        <span className="text-gray-400 hidden sm:inline">{stockData.industry || 'N/A'}</span>
                      </div>
                    </div>
                    <Button
                      data-testid="watchlist-toggle-button"
                      onClick={toggleWatchlist}
                      size="sm"
                      className={`h-7 text-xs ${inWatchlist ? 'bg-yellow-600 hover:bg-yellow-700 text-white' : 'bg-gray-700 hover:bg-gray-600 text-gray-300'}`}
                    >
                      {inWatchlist ? <Bookmark className="w-3 h-3 mr-1 fill-current" /> : <BookmarkPlus className="w-3 h-3 mr-1" />}
                      {inWatchlist ? "Saved" : "Watch"}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Finviz-Style Fundamentals Grid - Responsive */}
              <div className="grid grid-cols-3 md:grid-cols-6 divide-x divide-gray-800 text-[11px]">
                {/* NAV / Book Value badge — spans the row so the user can
                    compare the market price above against the fair-value
                    proxy at a glance.  ETFs get NAV; stocks get Book Value. */}
                <div className="col-span-3 md:col-span-6 p-2 border-b border-gray-800 flex items-center gap-2">
                  <NavBookValueBadge ticker={currentTicker} />
                </div>
                {/* Row 1 */}
                <div className="p-2">
                  <span className="text-gray-500">Market Cap</span>
                  <div className="font-semibold text-white" data-testid="market-cap">{formatNumber(stockData.marketCap)}</div>
                </div>
                <div className="p-2">
                  <span className="text-gray-500">P/E</span>
                  <div className="font-semibold text-white">{stockData.peRatio?.toFixed(2) || 'N/A'}</div>
                </div>
                <div className="p-2">
                  <span className="text-gray-500">EPS (ttm)</span>
                  <div className="font-semibold text-white">{stockData.eps?.toFixed(2) || 'N/A'}</div>
                </div>
                <div className="p-2">
                  <span className="text-gray-500">52W High</span>
                  <div className={`font-semibold ${stockData.currentPrice >= stockData.fiftyTwoWeekHigh * 0.95 ? 'text-green-400' : 'text-white'}`}>
                    ${stockData.fiftyTwoWeekHigh?.toFixed?.(2) || "0.00"}
                  </div>
                </div>
                <div className="p-2">
                  <span className="text-gray-500">52W Low</span>
                  <div className={`font-semibold ${stockData.currentPrice <= stockData.fiftyTwoWeekLow * 1.05 ? 'text-red-400' : 'text-white'}`}>
                    ${stockData.fiftyTwoWeekLow?.toFixed?.(2) || "0.00"}
                  </div>
                </div>
                <div className="p-2">
                  <span className="text-gray-500">Volume</span>
                  <div className="font-semibold text-white">{stockData.volume?.toLocaleString() || 'N/A'}</div>
                </div>
                {/* Row 2 */}
                <div className="p-2 border-t border-gray-800">
                  <span className="text-gray-500">Beta</span>
                  <div className="font-semibold text-white">{stockData.beta?.toFixed(2) || 'N/A'}</div>
                </div>
                <div className="p-2 border-t border-gray-800">
                  <span className="text-gray-500">Div Yield</span>
                  <div className="font-semibold text-white">{stockData.dividendYield ? `${Number(stockData.dividendYield).toFixed(2)}%` : 'N/A'}</div>
                </div>
                <div className="p-2 border-t border-gray-800">
                  <span className="text-gray-500">Avg Vol</span>
                  <div className="font-semibold text-white">{stockData.avgVolume?.toLocaleString() || 'N/A'}</div>
                </div>
                <div className="p-2 border-t border-gray-800">
                  <span className="text-gray-500">Open</span>
                  <div className="font-semibold text-white">${stockData.open?.toFixed?.(2) || "0.00"}</div>
                </div>
                <div className="p-2 border-t border-gray-800">
                  <span className="text-gray-500">Prev Close</span>
                  <div className="font-semibold text-white">${stockData.previousClose?.toFixed?.(2) || "0.00"}</div>
                </div>
                <div className="p-2 border-t border-gray-800">
                  <span className="text-gray-500">Day Range</span>
                  <div className="font-semibold text-white text-[10px]">
                    ${stockData.dayLow?.toFixed?.(2) || "0"} - ${stockData.dayHigh?.toFixed?.(2) || "0"}
                  </div>
                </div>
              </div>
            </div>

            {/* ETF Benchmark Gap — only renders for QQQ/SPY/DIA/IWM */}
            <BenchmarkGapCard ticker={currentTicker} />

            {/* Premarket High / Low / Open / Last — visible on every tab.
                Renders nothing when no premarket bars are available
                (weekends, very thin tickers, or pre-04:00 ET). */}
            <Suspense fallback={null}>
              <PremarketHighLowCard ticker={currentTicker} />
            </Suspense>

            {/* Finviz-Style Navigation Tabs - MOVED ABOVE Trading Profile */}
            <div className="bg-[#111] border border-gray-800 rounded">
              <div className="px-3 py-2 border-b border-gray-800">
                <span className="text-xs text-gray-500 uppercase tracking-wider">Analysis Tabs</span>
              </div>
              <div className="p-2">
                <Tabs 
                  defaultValue="charts" 
                  value={activeTab}
                  onValueChange={(value) => {
                    setActiveTab(value);
                    setLoadedTabs(prev => new Set([...prev, value]));
                  }}
                >
                  <TabsList className="flex flex-wrap gap-1 h-auto bg-transparent p-0">
                    <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white bg-gray-800 text-gray-400 hover:text-white px-2 py-1 text-[10px] rounded" data-testid="overview-tab">
                      <Activity className="w-3 h-3 mr-1" />
                      Overview
                    </TabsTrigger>
                    <TabsTrigger value="patterns" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white bg-gray-800 text-gray-400 hover:text-white px-2 py-1 text-[10px] rounded" data-testid="patterns-tab">
                      <BarChart3 className="w-3 h-3 mr-1" />
                      Patterns
                    </TabsTrigger>
                    <TabsTrigger value="options" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white bg-gray-800 text-gray-400 hover:text-white px-2 py-1 text-[10px] rounded" data-testid="options-tab">
                      <BarChart3 className="w-3 h-3 mr-1" />
                      Options
                    </TabsTrigger>
                    <TabsTrigger value="institutional" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white bg-gray-800 text-gray-400 hover:text-white px-2 py-1 text-[10px] rounded" data-testid="institutional-tab">
                      <Activity className="w-3 h-3 mr-1" />
                      Institutional
                    </TabsTrigger>
                    <TabsTrigger value="charts" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white bg-gray-800 text-gray-400 hover:text-white px-2 py-1 text-[10px] rounded" data-testid="charts-tab">
                      <LineChart className="w-3 h-3 mr-1" />
                      Charts
                    </TabsTrigger>
                    <TabsTrigger value="darkpool" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white bg-gray-800 text-gray-400 hover:text-white px-2 py-1 text-[10px] rounded" data-testid="darkpool-tab">
                      <Activity className="w-3 h-3 mr-1" />
                      Dark Pool
                    </TabsTrigger>
                    <TabsTrigger value="fundamentals" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white bg-gray-800 text-gray-400 hover:text-white px-2 py-1 text-[10px] rounded" data-testid="fundamentals-tab">
                      <BarChart3 className="w-3 h-3 mr-1" />
                      Fundamentals
                    </TabsTrigger>
                    <TabsTrigger value="prediction" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white bg-gray-800 text-gray-400 hover:text-white px-2 py-1 text-[10px] rounded" data-testid="prediction-tab">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      Prediction
                    </TabsTrigger>
                    <TabsTrigger value="contrarian" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white bg-gray-800 text-gray-400 hover:text-white px-2 py-1 text-[10px] rounded" data-testid="contrarian-tab">
                      <Users className="w-3 h-3 mr-1" />
                      Contrarian
                    </TabsTrigger>
                    <TabsTrigger value="news" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white bg-gray-800 text-gray-400 hover:text-white px-2 py-1 text-[10px] rounded" data-testid="news-tab">
                      <Newspaper className="w-3 h-3 mr-1" />
                      Company News
                    </TabsTrigger>
                    <TabsTrigger value="history60" className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white bg-gray-800 text-gray-400 hover:text-white px-2 py-1 text-[10px] rounded" data-testid="history60-tab">
                      <Clock className="w-3 h-3 mr-1" />
                      60 Day History
                    </TabsTrigger>
                    <TabsTrigger value="nextday" className="data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white bg-gray-800 text-gray-400 hover:text-white px-2 py-1 text-[10px] rounded" data-testid="nextday-tab">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      Next Day Direction
                    </TabsTrigger>
                    <TabsTrigger value="medge" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white bg-gray-800 text-gray-400 hover:text-white px-2 py-1 text-[10px] rounded" data-testid="medge-tab">
                      <Bookmark className="w-3 h-3 mr-1" />
                      Meaningful Edge
                    </TabsTrigger>
                    <TabsTrigger value="sentiment" className="data-[state=active]:bg-cyan-600 data-[state=active]:text-white bg-gray-800 text-gray-400 hover:text-white px-2 py-1 text-[10px] rounded" data-testid="sentiment-tab">
                      <Newspaper className="w-3 h-3 mr-1" />
                      Next Day Sentiment
                    </TabsTrigger>
                  </TabsList>

              <div className="mt-3 space-y-2">

              {/* Overview Tab - Lazy loaded */}
              <TabsContent value="overview" className="space-y-2">
                {loadedTabs.has('overview') && (
                  <>
                    {/* Fast AI Sentiment - Auto-loads */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <SentimentFast ticker={currentTicker} />
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                      <div className="bg-[#0d0d0d] rounded p-2">
                        <SentimentCard ticker={currentTicker} />
                      </div>
                      <div className="bg-[#0d0d0d] rounded p-2">
                        <Suspense fallback={<ComponentLoader />}>
                          <AnalystTargets ticker={currentTicker} />
                        </Suspense>
                      </div>
                    </div>

                    {/* Yahoo Finance Quote Widget */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <Suspense fallback={<ComponentLoader />}>
                        <YahooQuoteWidget ticker={currentTicker} />
                      </Suspense>
                    </div>

                    {/* News with Sentiment Analysis */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <Suspense fallback={<ComponentLoader />}>
                        <NewsSentiment ticker={currentTicker} />
                      </Suspense>
                    </div>
                  </>
                )}
              </TabsContent>

              {/* Trading Patterns Tab - Default, always loaded */}
              <TabsContent value="patterns" className="space-y-2">
                {/* Extended Hours Data - Pre-Market & After-Hours Gain/Loss */}
                <div className="bg-[#0d0d0d] rounded p-2">
                  <ExtendedHoursData ticker={currentTicker} />
                </div>
                
                {/* Valuation & Abnormal Activity Analysis - Above Trading Profile */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                  <div className="bg-[#0d0d0d] rounded p-2">
                    <Suspense fallback={<ComponentLoader />}>
                      <ValuationAnalysis ticker={currentTicker} />
                    </Suspense>
                  </div>
                  <div className="bg-[#0d0d0d] rounded p-2">
                    <Suspense fallback={<ComponentLoader />}>
                      <AbnormalActivity ticker={currentTicker} />
                    </Suspense>
                  </div>
                </div>

                {/* Real-Time Quote & Chart (TradingView Widget) - Ticker-specific */}
                <div className="bg-[#0d0d0d] rounded p-2">
                  <Suspense fallback={<ComponentLoader />}>
                    <TickLevelData ticker={currentTicker} />
                  </Suspense>
                </div>
                
                {/* Sector Performance - Market-wide sector analysis */}
                <div className="bg-[#0d0d0d] rounded p-2">
                  <Suspense fallback={<ComponentLoader />}>
                    <SectorPerformance />
                  </Suspense>
                </div>
                
                {/* Fast AI Sentiment - Displays first but waits for basket health data */}
                <div className="bg-[#0d0d0d] rounded p-2">
                  <SentimentFast ticker={currentTicker} basketHealthLoaded={basketHealthLoaded} />
                </div>
                
                {/* Comprehensive Sentiment Analysis - Above Intraday Options */}
                <div className="bg-[#0d0d0d] rounded p-2">
                  <Suspense fallback={<ComponentLoader />}>
                    <ComprehensiveSentiment ticker={currentTicker} />
                  </Suspense>
                </div>
                
                {/* Algorithmic Directional Predictive Analytics */}
                <div className="bg-[#0d0d0d] rounded p-2">
                  <Suspense fallback={<ComponentLoader />}>
                    <PredictiveAnalytics ticker={currentTicker} />
                  </Suspense>
                </div>
                
                {/* Intraday Options Trading Strategy - PRIMARY SIGNAL (fetches basket health) */}
                <div className="bg-[#0d0d0d] rounded p-2">
                  <Suspense fallback={<ComponentLoader />}>
                    <IntradayOptionsStrategy ticker={currentTicker} onBasketHealthLoaded={handleBasketHealthLoaded} />
                  </Suspense>
                </div>

                {/* Intraday Trading Signals - Price Chart with Trendlines & RSI/MFI (under Candlestick Chart) */}
                <Suspense fallback={<ComponentLoader />}>
                  <IntradayTradingSignals ticker={currentTicker} />
                </Suspense>
                
                {/* Today's Intraday Price Action Chart (under Candlestick Chart) */}
                <Suspense fallback={<ComponentLoader />}>
                  <IntradayPriceAction ticker={currentTicker} />
                </Suspense>
                
                {/* High-Probability Trading Signals - MFI+RSI+Trendlines+S/R */}
                <Suspense fallback={<ComponentLoader />}>
                  <TradingSignals ticker={currentTicker} />
                </Suspense>
                
                {/* Options Strategy Analyzer - COMPREHENSIVE */}
                <Suspense fallback={<ComponentLoader />}>
                  <OptionsStrategy ticker={currentTicker} />
                </Suspense>
                
                {/* Short-Term Risk Monitor (1-Day & 5-Day) */}
                <Suspense fallback={<ComponentLoader />}>
                  <ShortTermRisk ticker={currentTicker} />
                </Suspense>
                
                {/* Moving Averages Analysis */}
                <Suspense fallback={<ComponentLoader />}>
                  <MovingAverages ticker={currentTicker} />
                </Suspense>
                
                {/* Profit-Taking Risk Analysis */}
                <Suspense fallback={<ComponentLoader />}>
                  <ProfitTakingRisk ticker={currentTicker} />
                </Suspense>
                
                {/* Technical Indicators - RSI & MFI */}
                <Suspense fallback={<ComponentLoader />}>
                  <TechnicalIndicators ticker={currentTicker} />
                </Suspense>
                
                {/* Today's Probability Calculator */}
                <Suspense fallback={<ComponentLoader />}>
                  <TodaysProbability ticker={currentTicker} />
                </Suspense>
                
                {/* Weekly Patterns */}
                <Suspense fallback={<ComponentLoader />}>
                  <WeeklyPatterns ticker={currentTicker} />
                </Suspense>
                
                {/* Intraday and Monthly Patterns */}
                <div className="grid grid-cols-1 gap-6">
                  <Suspense fallback={<ComponentLoader />}>
                    <IntradayPatterns ticker={currentTicker} />
                  </Suspense>
                  <Suspense fallback={<ComponentLoader />}>
                    <MonthlyPatterns ticker={currentTicker} />
                  </Suspense>
                </div>
                
                {/* 5-Year Seasonal Monthly Analysis */}
                <Suspense fallback={<ComponentLoader />}>
                  <SeasonalMonthlyAnalysis ticker={currentTicker} />
                </Suspense>
              </TabsContent>

              {/* Options Tab - Lazy loaded */}
              <TabsContent value="options" className="space-y-2">
                {loadedTabs.has('options') && (
                  <div className="bg-[#0d0d0d] rounded p-2">
                    <Suspense fallback={<ComponentLoader />}>
                      <OptionsData ticker={currentTicker} />
                </Suspense>
                  </div>
                )}
              </TabsContent>

              {/* Institutional Tab - Lazy loaded */}
              <TabsContent value="institutional" className="space-y-2">
                {loadedTabs.has('institutional') && (
                  <>
                    {/* News with Sentiment Analysis */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <Suspense fallback={<ComponentLoader />}>
                        <NewsSentiment ticker={currentTicker} />
                      </Suspense>
                    </div>
                    
                    {/* Political Trading Activity */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <Suspense fallback={<ComponentLoader />}>
                        <PoliticalTrades ticker={currentTicker} />
                      </Suspense>
                    </div>
                    
                    {/* Two Column Layout */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
                      <div className="bg-[#0d0d0d] rounded p-2">
                        <Suspense fallback={<ComponentLoader />}>
                          <SECFilings ticker={currentTicker} />
                        </Suspense>
                      </div>
                      <div className="bg-[#0d0d0d] rounded p-2">
                        <Suspense fallback={<ComponentLoader />}>
                          <GovernmentContracts ticker={currentTicker} />
                        </Suspense>
                      </div>
                    </div>
                  </>
                )}
              </TabsContent>

              {/* Charts Tab - Lazy loaded */}
              <TabsContent value="charts" className="space-y-2">
                {loadedTabs.has('charts') && (
                  <div className="grid grid-cols-1 gap-2">
                    {/* Early Warnings — 4-layer scoring (D1-D18 / R1-R10 / BK1-BK18 / S1-S10) */}
                    <EarlyWarningsCard ticker={currentTicker} />

                    {/* StockPimp Predicts — 20-signal fusion (14 historical + 6 live overlays) */}
                    <StockPimpPredicts ticker={currentTicker} />

                    {/* Shared Timeframe Selector for All Charts (Simple version) */}
                    <div className="bg-[#0d0d0d] rounded p-2 flex items-center justify-between">
                      <span className="text-xs text-gray-400 font-medium">Chart Timeframe</span>
                      <div className="flex items-center gap-1 bg-[#111] rounded-lg p-1">
                        {[
                          { key: '1d',   label: '1D'   },
                          { key: '5d',   label: '5D'   },
                          { key: '10d',  label: '10D'  },
                          { key: '20d',  label: '20D'  },
                          { key: 'mtd',  label: 'MTD'  },
                          { key: '1mo',  label: '1M'   },
                          { key: '50d',  label: '50D'  },
                          { key: '3mo',  label: '3M'   },
                          { key: '100d', label: '100D' },
                          { key: '6mo',  label: '6M'   },
                          { key: '200d', label: '200D' },
                          { key: 'ytd',  label: 'YTD'  },
                          { key: '1y',   label: '1Y'   },
                          { key: '3y',   label: '3Y'   },
                          { key: '5y',   label: '5Y'   }
                        ].map(({ key, label }) => (
                          <button
                            key={key}
                            onClick={() => setChartTimeframe(key)}
                            className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                              chartTimeframe === key
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-400 hover:text-white hover:bg-gray-800'
                            }`}
                            data-testid={`shared-timeframe-${key}`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Quick-glance numerical returns strip for the current
                        ticker — mirrors the GICS table layout so you can
                        eyeball 1D…5Y momentum at a glance. */}
                    <QuickReturnsBar ticker={currentTicker} />

                    {/* Multi-Benchmark Comparison — Stock vs SPY/DIA/QQQ/IWM + sector & subsector ETFs,
                        with toggleable lines, regression, VWAP, and a per-benchmark diff box. */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <MultiBenchmarkChart ticker={currentTicker} timeframe={chartTimeframe} navBookValue={navBookValue} />
                      <ChartExplainChat ticker={currentTicker} timeframe={chartTimeframe} />
                      <OptionsAISuggestions ticker={currentTicker} />
                    </div>

                    {/* P/E Valuation — independent chart, loads with the page.
                        Stock vs industry-ETF top-10 peers + industry average. */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <PEValuationPanel ticker={currentTicker} timeframe={chartTimeframe} onValuation={setPeValuation} />
                    </div>

                    {/* Stock Indicators Chart - Price, MFI, RSI */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <Suspense fallback={<ComponentLoader />}>
                        <StockIndicatorsChart ticker={currentTicker} timeframe={chartTimeframe} />
                      </Suspense>
                    </div>
                    
                    {/* Stock VIX Chart - Price & VIX */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <Suspense fallback={<ComponentLoader />}>
                        <StockVixChart ticker={currentTicker} timeframe={chartTimeframe} />
                      </Suspense>
                    </div>
                    
                    {/* Price Trend Chart with Trendlines & Entry/Exit Zones */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <Suspense fallback={<ComponentLoader />}>
                        <PriceTrendChart 
                          ticker={currentTicker} 
                          timeframe={chartTimeframe}
                          timeframeChanges={timeframeChanges}
                        />
                      </Suspense>
                    </div>
                    
                    {/* Intraday Candlestick Chart - 1 Minute Intervals (LAZY: only mounts when scrolled near) */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <LazyOnScroll minHeight={420}>
                        <Suspense fallback={<ComponentLoader />}>
                          <IntradayCandlestick ticker={currentTicker} />
                        </Suspense>
                      </LazyOnScroll>
                    </div>
                    
                    {/* Intraday Time-of-Day Patterns - 5 Min Intervals over 60 Days (LAZY) */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <LazyOnScroll minHeight={500}>
                        <Suspense fallback={<ComponentLoader />}>
                          <IntradayPatternsChart ticker={currentTicker} />
                        </Suspense>
                      </LazyOnScroll>
                    </div>

                    {/* Intraday Periodicity Chart — companion to the dense
                        IntradayPatternsChart above. Surfaces the Heston-Korajczyk-Sadka
                        (2010) periodicity signal: today's price overlaid with the
                        historical mean return at each minute-of-day, plus a rolling
                        1-hour correlation showing how well today is tracking the
                        recurring intraday U-shape. */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <LazyOnScroll minHeight={420}>
                        <Suspense fallback={<ComponentLoader />}>
                          <IntradayPeriodicityChart ticker={currentTicker} />
                        </Suspense>
                      </LazyOnScroll>
                    </div>

                    {/* Intraday Confluence Map — full dashboard combining price,
                        VWAP+bands, volume profile, regime ribbon, CVD, and the
                        triple-confluence setup engine. Lives right under the
                        Periodicity chart so the trader sees regime + setup in
                        the same context as historical-pattern alignment. */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <LazyOnScroll minHeight={800}>
                        <Suspense fallback={<ComponentLoader />}>
                          <IntradayConfluenceMap ticker={currentTicker} />
                        </Suspense>
                      </LazyOnScroll>
                    </div>
                    
                    {/* Real-Time Quote from Yahoo Finance (LAZY) */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <LazyOnScroll minHeight={250}>
                        <Suspense fallback={<ComponentLoader />}>
                          <TickLevelData ticker={currentTicker} />
                        </Suspense>
                      </LazyOnScroll>
                    </div>
                    
                    {/* Technical Indicators from Yahoo Finance */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
                      <div className="bg-[#0d0d0d] rounded p-2">
                        <Suspense fallback={<ComponentLoader />}>
                          <TechnicalIndicators ticker={currentTicker} />
                        </Suspense>
                      </div>
                      <div className="bg-[#0d0d0d] rounded p-2">
                        <Suspense fallback={<ComponentLoader />}>
                          <MovingAverages ticker={currentTicker} />
                        </Suspense>
                      </div>
                    </div>
                    
                    {/* Intraday Price Action */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <Suspense fallback={<ComponentLoader />}>
                        <IntradayPriceAction ticker={currentTicker} />
                      </Suspense>
                    </div>

                    {/* Premarket Range Strategy — standalone 60-day backtest
                        chart. Shows total return, win rate, expectancy, max
                        drawdown, equity curve, and trade-by-trade table. */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <LazyOnScroll minHeight={500}>
                        <Suspense fallback={<ComponentLoader />}>
                          <PremarketRangeBacktest ticker={currentTicker} />
                        </Suspense>
                      </LazyOnScroll>
                    </div>
                    
                    {/* Premarket Pattern Analysis - Positive & Negative Premarket Trends */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <Suspense fallback={<ComponentLoader />}>
                        <PremarketPatternChart ticker={currentTicker} />
                      </Suspense>
                    </div>

                    {/* ETF Holdings — bottom of page, auto-hides for non-ETFs */}
                    <div className="bg-[#0d0d0d] rounded p-2">
                      <ETFHoldings ticker={currentTicker} onHoldingClick={(sym) => fetchStockData(sym)} />
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Dark Pool Tab - Lazy loaded */}
              <TabsContent value="darkpool" className="space-y-2">
                {loadedTabs.has('darkpool') && (
                <div className="bg-[#0d0d0d] rounded p-2">
                  <Suspense fallback={<ComponentLoader />}>
                    <DarkPoolData ticker={currentTicker} />
                  </Suspense>
                </div>
                )}
              </TabsContent>

              {/* Confluence Tab — full intraday dashboard (price + VWAP bands +
                  volume profile + regime ribbon + CVD + setup engine). */}
              <TabsContent value="confluence" className="space-y-2">
                {loadedTabs.has('confluence') && (
                  <Suspense fallback={<ComponentLoader />}>
                    <IntradayConfluenceMap ticker={currentTicker} />
                  </Suspense>
                )}
              </TabsContent>

              {/* Fundamentals Tab - Lazy loaded */}
              <TabsContent value="fundamentals" className="space-y-2">
                {loadedTabs.has('fundamentals') && (
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
                  <div className="bg-[#0d0d0d] rounded p-2">
                    <Suspense fallback={<ComponentLoader />}>
                      <Fundamentals ticker={currentTicker} />
                    </Suspense>
                  </div>
                  <div className="bg-[#0d0d0d] rounded p-2">
                    <Suspense fallback={<ComponentLoader />}>
                      <YahooQuoteWidget ticker={currentTicker} />
                    </Suspense>
                  </div>
                </div>
                )}
              </TabsContent>

              {/* Price Prediction Tab - Lazy loaded */}
              <TabsContent value="prediction" className="space-y-2">
                {loadedTabs.has('prediction') && (
                <div className="bg-[#0d0d0d] rounded p-2">
                  <PricePredictionCompact ticker={currentTicker} />
                </div>
                )}
              </TabsContent>

              {/* Contrarian Traders Tab - Lazy loaded */}
              <TabsContent value="contrarian" className="space-y-2">
                {loadedTabs.has('contrarian') && (
                <div className="bg-[#0d0d0d] rounded p-2">
                  <Suspense fallback={<ComponentLoader />}>
                    <ContrarianTraders ticker={currentTicker} />
                  </Suspense>
                </div>
                )}
              </TabsContent>

              {/* Company News Tab */}
              <TabsContent value="news" className="space-y-2">
                {loadedTabs.has('news') && (
                  <div className="bg-[#0d0d0d] rounded p-3">
                    <CompanyNews ticker={currentTicker} />
                  </div>
                )}
              </TabsContent>

              {/* 60 Day History Tab */}
              <TabsContent value="history60" className="space-y-2">
                {loadedTabs.has('history60') && (
                  <div className="bg-[#0d0d0d] rounded p-3">
                    <SixtyDayHistory ticker={currentTicker} />
                  </div>
                )}
              </TabsContent>

              {/* Next Day Direction Tab */}
              <TabsContent value="nextday" className="space-y-2">
                {loadedTabs.has('nextday') && (
                  <div className="bg-[#0d0d0d] rounded p-3">
                    <NextDayDirection ticker={currentTicker} />
                  </div>
                )}
              </TabsContent>

              {/* Meaningful Edge Tab */}
              <TabsContent value="medge" className="space-y-2">
                {loadedTabs.has('medge') && (
                  <div className="bg-[#0d0d0d] rounded p-3">
                    <MeaningfulEdge ticker={currentTicker} />
                  </div>
                )}
              </TabsContent>

              {/* Next Day Sentiment Prediction */}
              <TabsContent value="sentiment" className="space-y-2">
                {loadedTabs.has('sentiment') && (
                  <div className="bg-[#0d0d0d] rounded p-3">
                    <NextDaySentimentPrediction ticker={currentTicker} />
                  </div>
                )}
              </TabsContent>
              </div>
                </Tabs>
              </div>
            </div>

            {/* Trading Profile Settings - MOVED BELOW Analysis Tabs */}
            <div className="bg-[#111] border border-gray-800 rounded p-2">
              <TradingProfileSettings 
                onSettingsChange={handleTradingProfileChange}
                onRecalculate={handleRecalculateSignals}
                initialSettings={tradingProfile}
              />
            </div>

            {/* Master Signal - At-a-Glance Strip */}
            <div className="bg-[#111] border border-gray-800 rounded">
              <MasterSignal 
                key={signalRefreshKey}
                ticker={currentTicker}
                tradingProfile={tradingProfile}
                basketHealthData={basketHealthData}
              />
            </div>
          </div>
        ) : (
          <div className="text-center py-20 bg-[#111] rounded">
            <div className="max-w-md mx-auto">
              <Activity className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Search for a stock</h3>
              <p className="text-gray-400 text-sm">Enter a ticker symbol above to view comprehensive market data and AI-powered insights.</p>
            </div>
          </div>
        )}

        {/* Watchlist Sidebar - Finviz Style */}
        {watchlist.length > 0 && (
          <div className="mt-2 bg-[#111] border border-gray-800 rounded">
            <div className="px-3 py-2 border-b border-gray-800 flex items-center gap-2">
              <Bookmark className="w-4 h-4 text-yellow-500" />
              <span className="text-xs text-gray-400 uppercase tracking-wider">Watchlist</span>
            </div>
            <div className="p-2">
              <div className="flex flex-wrap gap-1">
                {watchlist.map((item) => (
                  <Button
                    key={item.ticker}
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSearchTicker(item.ticker);
                      fetchStockData(item.ticker);
                    }}
                    className="h-6 px-2 text-[11px] text-blue-400 hover:text-blue-300 hover:bg-gray-800"
                    data-testid={`watchlist-item-${item.ticker}`}
                  >
                    {item.ticker}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;